import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Heart, MessageCircle } from 'lucide-react';

const WHATSAPP_URL = 'https://wa.me/message/AIKG7VFJUUNMJ1';

const IMAGES = {
  soundHealing: 'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/vkqrk8xy_singing%20bowls.png',
  reiki: 'https://customer-assets.emergentagent.com/job_5bf56e5f-42e0-4cfb-a653-edc31a7a571c/artifacts/jbr2lcfd_Miri%20Reiki.JPG',
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

export const Community = () => {
  const { language } = useLanguage();

  return (
    <div data-testid="community-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.soundHealing}
            alt="Community Wellness"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-2xl"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#C9B99A] mb-4 font-['Manrope'] font-semibold">
              {language === 'es' ? 'Bienestar para Todos' : 'Wellness for Everyone'}
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-white mb-6 tracking-tight">
              {language === 'es' ? 'Bienestar Comunitario' : 'Community Wellness'}
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 md:py-32 px-6" data-testid="community-content">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center"
          >
            <motion.div variants={fadeInUp} className="w-20 h-20 rounded-full bg-[#F8F6F3] flex items-center justify-center mx-auto mb-8">
              <Heart className="w-10 h-10 text-[#A91B60]" />
            </motion.div>
            
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-8 tracking-tight">
              {language === 'es' ? 'Bienestar Comunitario' : 'Community Wellness'}
            </motion.h2>
            
            <motion.p variants={fadeInUp} className="text-xl text-[#5A5A5A] leading-relaxed mb-12 font-['Manrope'] max-w-3xl mx-auto">
              {language === 'es' 
                ? 'Como parte de nuestro compromiso con la comunidad, ofrecemos una sesión de bienestar semanal gratuita. Contáctanos para conocer fechas y disponibilidad.'
                : 'As part of our commitment to the community, we offer a complimentary weekly wellness session. Contact us for dates and availability.'}
            </motion.p>
            
            <motion.div variants={fadeInUp}>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-10 py-6 font-['Manrope'] font-semibold">
                  <MessageCircle className="mr-2 w-5 h-5" />
                  {language === 'es' ? 'Contáctanos' : 'Contact Us'}
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-16 px-6 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img
              src={IMAGES.reiki}
              alt="Community Wellness Session"
              className="w-full rounded-2xl shadow-xl aspect-[21/9] object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-3xl mx-auto text-center px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] mb-6 tracking-tight">
              {language === 'es' 
                ? <>Únete a nuestra <em className="italic text-[#A91B60]">comunidad</em></>
                : <>Join our <em className="italic text-[#A91B60]">community</em></>}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-[#5A5A5A] mb-10 font-['Manrope']">
              {language === 'es'
                ? 'Síguenos en redes sociales para enterarte de nuestros eventos comunitarios.'
                : 'Follow us on social media to stay updated on our community events.'}
            </motion.p>
            <motion.div variants={fadeInUp}>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full px-10 py-6 font-['Manrope'] font-semibold">
                  <MessageCircle className="mr-2 w-5 h-5" />
                  WhatsApp
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Community;
